import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Stack,
    IconButton,
    Tooltip,
    Alert
} from "@mui/material";
import {
    PersonAdd as PersonAddIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { useProfileCreateDialog } from "./hooks";
import {
    SearchSection,
    SelectedUserCard,
    SearchResultsList,
    DialogActionButtons
} from "./components";

interface ProfileCreateDialogProps {
    onProfileCreated?: (identity: string) => void;
}

export const ProfileCreateDialog: React.FC<ProfileCreateDialogProps> = ({ onProfileCreated }) => {
    const {
        open,
        openDialog,
        closeDialog,
        searchTerm,
        setSearchTerm,
        searchResults,
        isSearching,
        handleSearch,
        selectedUser,
        handleUserSelect,
        error,
        clearError,
        isCreating,
        handleCreateProfile,
        handleKeyPress
    } = useProfileCreateDialog({ onProfileCreated });

    return (
        <>
            {/* Trigger Button */}
            <Tooltip title="Create New Profile">
                <IconButton
                    onClick={openDialog}
                    sx={{
                        backgroundColor: '#162346',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: '#1a2a52',
                        },
                        width: 40,
                        height: 40
                    }}
                >
                    <PersonAddIcon />
                </IconButton>
            </Tooltip>

            {/* Dialog */}
            <Dialog
                open={open}
                onClose={closeDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonAddIcon sx={{ mr: 1, color: '#162346' }} />
                        <Typography variant="h6" component="span" fontWeight={600}>
                            Create New Profile
                        </Typography>
                    </Box>
                    <IconButton onClick={closeDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={3}>
                        <SearchSection
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            onSearch={handleSearch}
                            onKeyPress={handleKeyPress}
                            isSearching={isSearching}
                        />

                        {error && (
                            <Alert severity="error" onClose={clearError}>
                                {error}
                            </Alert>
                        )}

                        {selectedUser && (
                            <SelectedUserCard user={selectedUser} />
                        )}

                        <SearchResultsList
                            results={searchResults}
                            selectedUser={selectedUser}
                            onUserSelect={handleUserSelect}
                        />
                    </Stack>
                </DialogContent>

                <DialogActionButtons
                    onCancel={closeDialog}
                    onCreate={handleCreateProfile}
                    isCreating={isCreating}
                    isCreateDisabled={!selectedUser}
                />
            </Dialog>
        </>
    );
};
